import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ProviderCard } from '~/components/features/integrations/provider-card';
import { PageContainer } from '~/components/layouts/page-container';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { useSlugIds } from '~/hooks/use-slug-ids';
import { resolveOrgId } from '~/lib/slug-resolver';
import { atsConfigQueryOptions } from '~/queries/integrations.queries';

export const Route = createFileRoute('/_authenticated/org/$orgSlug/integrations/')({
  loader: ({ context, params }) => {
    const orgId = resolveOrgId(context.queryClient, params.orgSlug);
    if (orgId) {
      void context.queryClient.prefetchQuery(atsConfigQueryOptions(orgId));
    }
  },
  component: IntegrationsPage
});

function IntegrationsPage(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t('integrations.title'));
  const { orgSlug } = Route.useParams();
  const { orgId } = useSlugIds(orgSlug);
  const { data } = useQuery({ ...atsConfigQueryOptions(orgId), enabled: !!orgId });
  const config = data?.config;

  const isTeamtailor = config?.provider === 'teamtailor' && config.hasApiKey;
  const isRecman = config?.provider === 'recman' && config.hasApiKey;
  const isLogicMelon = config?.provider === 'logicmelon' && config.hasApiKey;

  return (
    <PageContainer
      breadcrumbs={[{ label: t('integrations.title') }]}
      title={t('integrations.title')}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <ProviderCard
          to="/org/$orgSlug/integrations/teamtailor"
          params={{ orgSlug }}
          name={t('integrations.ats.providers.teamtailor')}
          description={t('integrations.ats.providers.teamtailorDescription')}
          logoSrc="/integrations/teamtailor.png"
          isConnected={isTeamtailor}
          connectedLabel={t('integrations.ats.verifyStatus.valid')}
        />
        <ProviderCard
          to="/org/$orgSlug/integrations/recman"
          params={{ orgSlug }}
          name={t('integrations.ats.providers.recman')}
          description={t('integrations.ats.providers.recmanDescription')}
          logoSrc="/integrations/recman.png"
          isConnected={isRecman}
          connectedLabel={t('integrations.ats.verifyStatus.valid')}
        />
        <ProviderCard
          to="/org/$orgSlug/integrations/logicmelon"
          params={{ orgSlug }}
          name={t('integrations.ats.providers.logicmelon')}
          description={t('integrations.ats.providers.logicmelonDescription')}
          logoSrc="/integrations/logicmelon.webp"
          isConnected={isLogicMelon}
          connectedLabel={t('integrations.ats.verifyStatus.valid')}
        />
      </div>
    </PageContainer>
  );
}
